import { useMutation } from '@tanstack/react-query'

import { Logger } from '@core/logger'
import { queryClient } from '@core/query'
import { FollowQueryKey } from '../helpers/queryKeys'
import { resetFollowers, resetFollowing } from '../helpers/followCache'

import { deleteFollow } from '../services/deleteFollow'
import {
    applyFollowState,
    restoreFollowState,
    snapshotFollowState,
    type TFollowSnapshot,
} from '../helpers/followOptimistic'
import type { TFollowVariables } from './useFollow'

type TUnfollowResult = { date_unfollowed: number }

/**
 * Unfollow a user. Mirror of {@link useFollow}: optimistic flag/count decrement with rollback, list
 * invalidation on success. Resolves with `date_unfollowed` so callers can seed the feed's
 * `unfollow_threshold`.
 */
export const useUnfollow = () => {
    return useMutation<TUnfollowResult, Error, TFollowVariables, TFollowSnapshot>({
        mutationFn: ({ username }) => deleteFollow(username),
        onMutate: async ({ username, myUsername }) => {
            // Cancel every query the optimistic update overwrites (flag + both stat counts) — an
            // in-flight fetch resolving after our setData would otherwise clobber the optimistic value.
            await Promise.all([
                queryClient.cancelQueries({ queryKey: FollowQueryKey.followCheck(username) }),
                queryClient.cancelQueries({ queryKey: FollowQueryKey.followStatistics(username) }),
                queryClient.cancelQueries({
                    queryKey: FollowQueryKey.followStatistics(myUsername),
                }),
            ])

            const snapshot = snapshotFollowState(username, myUsername)
            applyFollowState(username, myUsername, false)

            return snapshot
        },
        onError: (error, { username, myUsername }, snapshot) => {
            Logger.error('Unfollow error', { error })

            if (snapshot) {
                restoreFollowState(username, myUsername, snapshot)
            }
        },
        onSuccess: (_data, { username, myUsername }) => {
            resetFollowers(username)
            resetFollowing(myUsername)
        },
    })
}
